/**
 * Where a notification opens.
 *
 * A notification carries a REFERENT — subject_type, subject_id, audit_id — and never
 * a URL. Three front ends have three route tables on three origins, so a stored
 * '/graph?node=x' is meaningless in the developer portal and rots the first time a
 * route is renamed. The row says what it is about; each surface decides where that
 * lives.
 *
 * SCOPE, deliberately narrow, in the spirit of shared/auth: this is the map and the
 * space rule, nothing else. It does no fetching, knows no React, and returns an
 * intent rather than navigating — the caller owns the router.
 *
 * subject_type values are AuditLog.resource_type values (see backend ResourceTypes),
 * because the audit bridge fills the referent straight off the audit row.
 */

// Mirrors GROUP_BEARING_PATHS in frontend/src/contexts/GroupContext.js. A group rides
// in the PATH for these and in a query parameter for everything else; getting that
// wrong is how personal and group data end up sharing a screen.
//
// NUCLEUS ONLY. This is Nucleus's grammar and no one else's — see SPACE_RULES.
export const GROUP_BEARING_PATHS = [
  '/graph', '/schemas', '/privacy-realms', '/exposure-profiles', '/applications',
  '/apps', '/plugins', '/automations', '/people', '/manage', '/activity',
];

// HOW EACH SURFACE CARRIES A GROUP. Three apps, three answers, and applying one
// of them to all three is what sent a group-owned app's key rotation to a URL
// the developer portal does not have.
//
//   nucleus    in the path for GROUP_BEARING_PATHS, ?group_id= for the rest
//   developer  nowhere in the URL at all — sessionStorage.activeGroupId, which
//              the caller sets when mustSwitchSpace is true, then navigates
//   admin      has no groups; a row there is platform-wide or personal
const SPACE_RULES = {
  nucleus: (path, params, space) => {
    if (GROUP_BEARING_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) {
      return `/group/${encodeURIComponent(space)}${path}`;
    }
    params.group_id = space;
    return path;
  },
  developer: (path) => path,
  admin: (path) => path,
};

const nucleus = {
  node:               (r) => ({ path: '/graph', query: { node: r.subject_id } }),
  relationship:       (r) => ({ path: '/graph', query: { node: r.subject_id } }),
  proposal:           (r) => ({ path: '/graph', query: { tab: 'proposals', proposal: r.subject_id } }),
  question:           (r) => ({ path: '/graph', query: { tab: 'questions', question: r.subject_id } }),
  profile:            (r) => ({ path: '/self', query: { leaf: 'profile', identity: r.subject_id } }),
  // A password change, a new sign-in, replaced recovery codes: rows about how you
  // get in, and Security is where you answer them. They name no subject id —
  // there is no "it" to open — which is why they are SUBJECTLESS below.
  auth:               ()  => ({ path: '/self', query: { leaf: 'security' } }),
  // A developer token acts as you; the ledger line about one sits with the
  // rest of how you get in. Its own list is the developer portal's, another
  // origin this router cannot reach.
  developer_token:    ()  => ({ path: '/self', query: { leaf: 'security' } }),
  // A grant opens as its contract (the page reads ?open=<ep id>). '/applications?ep='
  // was read by nothing, so app authorized/revoked rows and expiry reminders opened
  // the Apps page with nothing selected.
  authorization:      (r) => ({ path: '/realms/profiles', query: { open: r.subject_id } }),
  exposure_profile:   (r) => ({ path: '/realms/profiles', query: { open: r.subject_id } }),
  third_party:        (r) => ({ path: '/applications', query: { app: r.subject_id } }),
  // A plugin that wants you (its approval lapsed, its account was revoked, a
  // schedule stopped) opens with its inspector up (?open=). A stopped schedule
  // opens its tool on the Schedule tab, where the fix is.
  plugin:             (r) => ({
    path: '/plugins',
    query: {
      open: r.subject_id,
      ...(r.payload?.tool_id ? { tool: r.payload.tool_id } : {}),
      ...(r.notification_type === 'plugin_schedule_blocked' ? { tab: 'schedule' } : {}),
    },
  }),
  automation:         (r) => ({ path: '/automations', query: { automation: r.subject_id } }),
  // A share began or ended: open the person (payload.user_id). '?tab=shared' was read
  // by nothing, so the row landed on Friends with nobody open.
  sharing:            (r) => ({ path: '/people', query: r.payload?.user_id ? { open: r.payload.user_id } : {} }),
  // A follow request is answered on Following, a friend request on Friends, with the
  // person who asked open (payload.user_id). Older rows name only the request, which
  // People turns into its person while the request is still there.
  follow:             (r) => ({
    path: r.payload?.relationship_type === 'follow' ? '/people/following' : '/people',
    query: r.payload?.user_id ? { open: r.payload.user_id } : r.subject_id ? { request: r.subject_id } : {},
  }),
  // Two kinds of row say 'group'. One addressed to the GROUP (a member joined,
  // left, was promoted) carries the group and opens its members. One addressed
  // to YOU (you were added, removed, made an admin; it was deleted) is about
  // your membership, and opens Groups — People in your own space is Friends,
  // which was the answer to a question nobody asked.
  group:              (r) => ({ path: (r.group_id || payloadGroupOf(r)) ? '/people' : '/groups', query: {} }),
  // An invitation's referent is genuinely two ids: the invitation to answer and the
  // group it is for. The consent screen needs both.
  group_invitation:   (r) => ({
    path: '/grant',
    query: { to_group_id: r.payload?.group_id, invitation_id: r.subject_id },
  }),
  // A run an app or agent asked for, Waiting for you: it opens on Runs, with
  // that request up (?request=), where Allow and Decline are. Without a map
  // entry it fell through to the app page via third_party_id.
  plugin_request:     (r) => ({ path: '/plugins/runs', query: { request: r.subject_id } }),
  // "Your import finished: 40 created." What it made is in the graph.
  import_job:         ()  => ({ path: '/graph', query: {} }),
  // The retention sweep destroyed what sat in the trash past its window. The
  // nodes are gone; the trash is where what is left of it can still be saved.
  trash:              ()  => ({ path: '/graph', query: { tab: 'trash' } }),
};

const developer = {
  third_party:        (r) => ({ path: `/applications/${r.subject_id}`, query: {} }),
  app_key:            (r) => ({ path: `/applications/${r.subject_id}`, query: { panel: 'keys' } }),
  webhook:            (r) => ({ path: `/applications/${r.subject_id}`, query: { panel: 'webhooks' } }),
  schema:             (r) => ({ path: '/schemas', query: { schema: r.subject_id } }),
  // "Payouts are on hold … open Money in the developer portal to finish." The
  // row said where to go and could not go there: no map entry, no app id on
  // the row, no audit id, and the developer fallback is null — so the plate
  // rendered it inert. Money lives at /billing.
  payout_account:     ()  => ({ path: '/billing', query: {} }),
};

const admin = {
  user:               (r) => ({ path: '/users', query: { user: r.subject_id } }),
  third_party:        (r) => ({ path: '/apps', query: { app: r.subject_id } }),
  auth:               (r) => ({ path: '/login-attempts', query: r.subject_id ? { ip: r.subject_id } : {} }),
  rate_limit:         ()  => ({ path: '/logs', query: {} }),
};

const MAPS = { nucleus, developer, admin };

// Subjects whose destination does not depend on WHICH one: the row names a
// kind of thing (how you sign in, the trash) rather than an item. Requiring an
// id of these is what left a password change and a trash purge linking nowhere.
const SUBJECTLESS = new Set(['auth', 'trash']);
// A row whose destination is a PERSON, named in its payload: "Your friendship
// with Mira ended" has no one contract to name (both sides ended), and needed
// a subject id it could not carry to open Mira at all.
const namesPerson = (row) => row.subject_type === 'sharing' && !!row.payload?.user_id;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The group a row's PAYLOAD says it is about, or null. Only the platform's own
 * rows are believed (notification_service.group_payload: `group_id` with
 * `in_group: true`), and only a UUID: an app writes its rows' payload freely
 * (POST /third-party/notifications), and a group_id of '../self?leaf=security#'
 * spliced into the path sent a click on its row wherever the app liked.
 */
export function payloadGroupOf(row) {
  const p = row?.payload;
  if (!p || typeof p !== 'object' || row.source === 'third_party') return null;
  if (p.in_group !== true) return null;
  const gid = p.group_id == null ? '' : String(p.group_id);
  return UUID.test(gid) ? gid : null;
}

// Where a row goes when its subject is gone, or was never mapped. The event itself
// still happened, and the record of it is the honest destination — a notification
// that 404s is worse than one that does not move.
const FALLBACK = {
  nucleus: (r) => ({ path: '/activity', query: { audit: r.audit_id } }),
  developer: () => null,
  admin: () => ({ path: '/logs', query: {} }),
};

/**
 * Resolve a notification to a route intent on one surface.
 *
 * Returns { path, query, space } or null. Null means this row has no destination on
 * this surface, which is a bug in whoever minted it rather than a blank to render:
 * a row belongs to exactly one surface, and that surface should know where its
 * subjects live.
 */
export function resolve(row, surface = 'nucleus') {
  if (!row) return null;

  const map = MAPS[surface];
  if (!map) return null;

  // A row about a group's proposal or node may name its group only in the
  // payload; reading row.group_id alone opened those in the personal space. An
  // invitation is the exception: its payload names the group being OFFERED, which
  // the person is not in yet, and its consent screen is answered from where they
  // are — and it carries no `in_group`, so payloadGroupOf passes it by.
  const payloadGroup = payloadGroupOf(row);
  const space = row.group_id || payloadGroup || 'personal';
  const build = row.subject_type ? map[row.subject_type] : null;

  let intent = null;
  if (build && (row.subject_id || SUBJECTLESS.has(row.subject_type) || namesPerson(row))) {
    intent = build(row);
  } else if (row.audit_id) {
    intent = FALLBACK[surface](row);
  } else if (row.third_party_id && map.third_party) {
    // LAST RESORT: the app that caused it. Thousands of rows predate the referent
    // columns and carry nothing but a third_party_id — but a row that names an app
    // can always open that app, which is a truthful answer to "what was this?" even
    // when the thing it happened to is no longer identifiable.
    intent = map.third_party({ ...row, subject_id: row.third_party_id });
  }

  if (!intent) return null;
  return { ...intent, space };
}

/**
 * Turn an intent into a URL, carrying the space the way THIS SURFACE carries it.
 *
 * A row from a group opens in that group: the caller switches space first when
 * `mustSwitchSpace` is true, then navigates. On Nucleus the switch is implied by
 * the URL; on the developer portal the URL says nothing and the switch is the
 * whole mechanism, so a caller there that ignores mustSwitchSpace opens the
 * right page in the wrong group.
 */
export function applySpace(intent, currentSpace = 'personal', surface = 'nucleus') {
  if (!intent) return null;

  const { path, query = {}, space = 'personal' } = intent;
  const params = { ...query };
  let url = path;

  if (space && space !== 'personal') {
    const carry = SPACE_RULES[surface] || SPACE_RULES.nucleus;
    url = carry(path, params, space);
  }

  const search = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

  return {
    url: search ? `${url}?${search}` : url,
    space,
    mustSwitchSpace: space !== currentSpace,
  };
}

/** resolve + applySpace, for callers that only want somewhere to go. */
export function hrefFor(row, surface = 'nucleus', currentSpace = 'personal') {
  return applySpace(resolve(row, surface), currentSpace, surface);
}
